<template>
  <div class="locations container container--page-content flex flex-col gap-12 lg:gap-20">
    <div class="locations__header flex flex-col gap-5 lg:gap-6">
      <h2 class="locations__title">{{ component.locationsTitle }}</h2>
      <p class="locations__description text-regular-normal">{{ component.locationsDescription }}</p>
    </div>
    <div class="locations__content lg:grid lg:grid-cols-[9fr_11fr] gap-20">
      <div class="locations__list flex flex-col gap-10">
        <div
          class="locations__item lg:pl-8 lg:border-l lg:border-accent lg:border-opacity-0 transition-all duration-500 ease-in-out"
          :class="{ 'lg:!border-opacity-100': index === activeIndex }"
          v-for="(location, index) in component.locations || []"
          :key="`locations-${index}`"
          @mouseenter="activeIndex = index"
        >
          <div class="locations__item-image mb-12 lg:hidden rounded-md overflow-hidden">
            <SharedPicture
              class="w-full !rounded-md overflow-hidden block !max-w-[500px] !h-[268px] object-cover object-center"
              :media="location.picture"
              height="268"
              width="500"
              format="webp"
              :quality="85"
            />
          </div>
          <div class="locations__item-content flex flex-col gap-4">
            <h3 class="h5 locations__item-title">{{ location.title }}</h3>
            <div class="locations__item-description text-regular-normal flex items-center gap-4" v-if="location.address">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="locations__item-description-icon-svg text-accent">
                <path
                  d="M21.447 6.10498L15.447 3.10498C15.3081 3.03546 15.1549 2.99927 14.9995 2.99927C14.8441 2.99927 14.6909 3.03546 14.552 3.10498L9 5.88198L3.447 3.10498C3.2945 3.02878 3.12506 2.99283 2.95476 3.00054C2.78446 3.00825 2.61895 3.05938 2.47397 3.14905C2.32899 3.23873 2.20933 3.36398 2.12638 3.51291C2.04342 3.66184 1.99992 3.82951 2 3.99998V17C2 17.379 2.214 17.725 2.553 17.895L8.553 20.895C8.69193 20.9645 8.84515 21.0007 9.0005 21.0007C9.15585 21.0007 9.30907 20.9645 9.448 20.895L15 18.118L20.553 20.894C20.7051 20.9709 20.8744 21.0074 21.0446 20.9998C21.2149 20.9923 21.3803 20.941 21.525 20.851C21.82 20.668 22 20.347 22 20V6.99998C22 6.62098 21.786 6.27498 21.447 6.10498ZM10 7.61798L14 5.61798V16.382L10 18.382V7.61798ZM4 5.61798L8 7.61798V18.382L4 16.382V5.61798ZM20 18.382L16 16.382V5.61798L20 7.61798V18.382Z"
                  fill="currentColor"
                />
              </svg>
              <address class="locations__item-description text-regular-normal not-italic">
                {{ location.address }}
              </address>
            </div>
            <a
              @click="trackPhoneClick(location.phone, location.title)"
              :href="`tel:${location.phone}`"
              class="locations__item-description text-regular-normal flex items-center gap-4 hover:text-accent hover:underline"
              :aria-label="t('components.locations.phone', { phone: location.phone })"
              :title="t('components.locations.phone', { phone: location.phone })"
              v-if="location.phone"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="locations__item-description-icon-svg text-accent">
                <path
                  d="M17.7073 12.293C17.6145 12.2 17.5043 12.1263 17.383 12.076C17.2617 12.0257 17.1317 11.9998 17.0003 11.9998C16.869 11.9998 16.739 12.0257 16.6176 12.076C16.4963 12.1263 16.3861 12.2 16.2933 12.293L14.6993 13.887C13.9603 13.667 12.5813 13.167 11.7073 12.293C10.8333 11.419 10.3333 10.04 10.1133 9.30096L11.7073 7.70696C11.8003 7.61417 11.874 7.50397 11.9243 7.38265C11.9746 7.26134 12.0005 7.13129 12.0005 6.99996C12.0005 6.86862 11.9746 6.73858 11.9243 6.61726C11.874 6.49595 11.8003 6.38575 11.7073 6.29296L7.70733 2.29296C7.61455 2.20001 7.50434 2.12627 7.38303 2.07596C7.26171 2.02565 7.13167 1.99976 7.00033 1.99976C6.869 1.99976 6.73896 2.02565 6.61764 2.07596C6.49633 2.12627 6.38612 2.20001 6.29333 2.29296L3.58133 5.00496C3.20133 5.38496 2.98733 5.90696 2.99533 6.43996C3.01833 7.86396 3.39533 12.81 7.29333 16.708C11.1913 20.606 16.1373 20.982 17.5623 21.006H17.5903C18.1183 21.006 18.6173 20.798 18.9953 20.42L21.7073 17.708C21.8003 17.6152 21.874 17.505 21.9243 17.3837C21.9746 17.2623 22.0005 17.1323 22.0005 17.001C22.0005 16.8696 21.9746 16.7396 21.9243 16.6183C21.874 16.4969 21.8003 16.3867 21.7073 16.294L17.7073 12.293ZM17.5803 19.005C16.3323 18.984 12.0623 18.649 8.70733 15.293C5.34133 11.927 5.01533 7.64196 4.99533 6.41896L7.00033 4.41396L9.58633 6.99996L8.29333 8.29296C8.1758 8.41041 8.08938 8.55529 8.04189 8.71453C7.9944 8.87376 7.98733 9.04231 8.02133 9.20496C8.04533 9.31996 8.63233 12.047 10.2923 13.707C11.9523 15.367 14.6793 15.954 14.7943 15.978C14.9569 16.0129 15.1256 16.0064 15.285 15.9591C15.4444 15.9117 15.5893 15.825 15.7063 15.707L17.0003 14.414L19.5863 17L17.5803 19.005Z"
                  fill="currentColor"
                />
              </svg>
              <span>
                {{ location.phone }}
              </span>
            </a>
            <a
              @click="trackEmailClick(location.email, location.title)"
              :href="`mailto:${location.email}`"
              class="locations__item-description text-regular-normal flex items-center gap-4 hover:text-accent hover:underline"
              v-if="location.email"
              :aria-label="t('components.locations.email', { email: location.email })"
              :title="t('components.locations.email', { email: location.email })"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="locations__item-description-icon-svg text-accent">
                <path
                  d="M20 4H4C2.897 4 2 4.897 2 6V18C2 19.103 2.897 20 4 20H20C21.103 20 22 19.103 22 18V6C22 4.897 21.103 4 20 4ZM20 6V6.511L12 12.734L4 6.512V6H20ZM4 18V9.044L11.386 14.789C11.5611 14.9265 11.7773 15.0013 12 15.0013C12.2227 15.0013 12.4389 14.9265 12.614 14.789L20 9.044L20.002 18H4Z"
                  fill="currentColor"
                />
              </svg>
              <span>
                {{ location.email }}
              </span>
            </a>
            <SharedButton
              @click="trackGoogleMapsClick(location.title)"
              :to="location.googleMapsLink || ''"
              :label="t('components.locations.viewOnGoogleMaps')"
              class="locations__item-link flex items-center gap-2 w-max"
              iconPosition="right"
              variant="primary"
            >
              <template #icon>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="locations__item-link-icon-svg text-white">
                  <path d="M11.293 17.293L12.707 18.707L19.414 12L12.707 5.29297L11.293 6.70697L15.586 11H6V13H15.586L11.293 17.293Z" fill="currentColor" />
                </svg>
              </template>
            </SharedButton>
          </div>
        </div>
      </div>
      <div class="locations__desktop-images hidden lg:flex">
        <SharedPicture
          v-for="(location, index) in component.locations"
          :key="index"
          :media="location.picture"
          :loading="index === activeIndex ? 'eager' : 'lazy'"
          :class="{ 'locations__desktop-image--active': index === activeIndex }"
          class="locations__desktop-image"
          height="685"
          width="625"
          format="webp"
          :quality="85"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { GraphQLLocations } from '~/types/graphql';

  interface EventData {
    event: 'locations_phone_click' | 'locations_email_click' | 'locations_google_maps_click';
    location_title: string;
    page_location: string;
  }

  const { t } = useI18n();

  defineProps<{
    component: GraphQLLocations;
    index: number;
  }>();

  const activeIndex = ref(0);

  const { push } = useDataLayer();

  const trackPhoneClick = (phone: string, title: string) => {
    const eventData: EventData = {
      event: 'locations_phone_click',
      location_title: title,
      page_location: window.location.href,
    };
    push(eventData);
  };

  const trackEmailClick = (email: string, title: string) => {
    const eventData: EventData = {
      event: 'locations_email_click',
      location_title: title,
      page_location: window.location.href,
    };
    push(eventData);
  };

  const trackGoogleMapsClick = (title: string) => {
    const eventData: EventData = {
      event: 'locations_google_maps_click',
      location_title: title,
      page_location: window.location.href,
    };
    push(eventData);
  };
</script>

<style lang="scss" scoped>
  .locations {
    &__desktop-images {
      @apply relative w-full h-full;
    }
    &__desktop-image {
      @apply absolute w-full h-full top-0 left-0;
      @apply opacity-0;
      @apply transition-opacity duration-[1000ms] ease-in-out;
      @apply object-cover object-center;
      max-width: 100%;
      &:deep(img) {
        @apply w-full h-full object-cover object-center;
        @apply rounded-md overflow-hidden;
        max-width: 100%;
      }
      &--active {
        @apply opacity-100;
      }
    }
  }
</style>
